import { formatMarketMoney } from '@/components/helpers/utils'
import { getMarketPrice } from '@/components/helpers/market-price'

const FILTERS_BLACKLIST = [
]

const RANGE_INDICATOR = '<->'

/**
 * Price filter field candidates. Which one the index actually range-filters
 * and facets on is an AMC/feed mapping decision, not a code one — hardcoding
 * a single field name (the pre-migration `ss_price`) is what breaks the price
 * slider when the index populates `price` or `ss_price_range` instead.
 */
const PRICE_FILTER_FIELDS = ['price', 'ss_price_range', 'ss_price']

const isPriceFilterField = (field) => PRICE_FILTER_FIELDS.includes(field)

const parseFiltersFromResults = (results) => {
  if (!results?.facets) return { fetchedFilters: [], sizeFilters: [] }
  const whiteListedFilters = results.facets.filter((facet) => {
    return !FILTERS_BLACKLIST.some((field) => field === facet.field)
  })
  return whiteListedFilters
}

/**
 * Parse the currently-applied range for a filter field out of the active
 * filters set, so the slider thumbs and the collapsed header label reflect
 * what's actually applied — including on a reloaded or shared URL, before any
 * live facet has come back (or when the live facet's range is unpopulated).
 * @returns {[number, number] | null}
 */
const parseActiveRange = (field, activeFilters) => {
  if (!field || !activeFilters) return null

  for (const activeFilter of activeFilters) {
    if (!activeFilter.startsWith(`${field}:`)) continue

    const value = activeFilter.slice(field.length + 1)
    if (!value.includes(RANGE_INDICATOR)) continue

    const [low, high] = value.split(RANGE_INDICATOR).map(parseFloat)
    if (isNaN(low) || isNaN(high)) continue

    return [low, high]
  }

  return null
}

/**
 * Format a facet's base-currency price value for display — never with an
 * API-supplied format string, so multi-currency stores render correctly regardless
 * of what the facet response says.
 *
 * On a market with its own pricing the label is an actual market price recorded
 * while localising the results, rounded to the whole unit. Only the label changes:
 * the value sent back as a range filter stays in base currency, matching the index.
 */
const formatPriceValue = (value) => {
  const marketPrice = getMarketPrice(value)

  if (marketPrice === null) {
    return formatMarketMoney(value * 100, { decimals: value % 1 !== 0 })
  }

  return formatMarketMoney(Math.round(marketPrice) * 100, { decimals: false })
}

export { parseFiltersFromResults, PRICE_FILTER_FIELDS, isPriceFilterField, parseActiveRange, formatPriceValue, RANGE_INDICATOR }
